import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { FileText, CheckCircle, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function InvoiceView() {
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchInvoice();
  }, [id]);

  const fetchInvoice = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/invoice/${id}/view`);
      setInvoice(response.data);
    } catch (error) {
      setError('Invoice not found');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading invoice...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Invoice Not Found</h2>
            <p className="text-muted-foreground">This invoice link may be invalid or expired.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-primary font-heading">VYRUS HUB</h1>
          <p className="text-muted-foreground text-sm">Invoice</p>
        </div>

        {/* Invoice Card */}
        <Card className="bg-card border-border/50">
          <CardHeader className="border-b border-border pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Invoice Number</p>
                <CardTitle className="text-xl font-mono">{invoice.invoice_number}</CardTitle>
              </div>
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                invoice.status === 'paid' 
                  ? 'bg-green-500/20 text-green-400' 
                  : 'bg-yellow-500/20 text-yellow-400'
              }`}>
                {invoice.status === 'paid' ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  <Clock className="h-4 w-4" />
                )}
                {invoice.status.toUpperCase()}
              </div>
            </div>
          </CardHeader>

          <CardContent className="pt-6 space-y-6">
            {/* Customer Info */}
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Bill To</p>
              <p className="font-semibold text-lg">{invoice.customer_name}</p>
              <p className="text-muted-foreground text-sm">{invoice.customer_email}</p>
            </div>

            {/* Amount */}
            <div className="bg-primary/10 p-6 rounded-lg text-center">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Amount Due</p>
              <p className="text-4xl font-bold text-primary font-mono">
                {formatCurrency(invoice.amount)}
              </p>
            </div>

            {/* Notes */}
            {invoice.notes && (
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Notes</p>
                <p className="text-sm bg-secondary/30 p-3 rounded-lg">{invoice.notes}</p>
              </div>
            )}

            {/* Payment Methods */}
            {invoice.status === 'unpaid' && (
              <div className="border-t border-border pt-6">
                <p className="text-sm font-semibold mb-4 text-center">Payment Methods</p>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-blue-500/10 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">💳</span>
                      <span className="font-medium">Zelle</span>
                    </div>
                    <span className="font-mono text-sm bg-secondary/50 px-2 py-1 rounded">516-424-4367</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-500/10 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">🍎</span>
                      <span className="font-medium">Apple Pay</span>
                    </div>
                    <span className="font-mono text-sm bg-secondary/50 px-2 py-1 rounded">516-424-4367</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">💵</span>
                      <span className="font-medium">Cash App</span>
                    </div>
                    <span className="font-mono text-sm bg-secondary/50 px-2 py-1 rounded">$WallaceOz</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground text-center mt-4">
                  Please include invoice number <span className="font-mono">{invoice.invoice_number}</span> with your payment
                </p>
              </div>
            )}

            {/* Paid Status */}
            {invoice.status === 'paid' && (
              <div className="border-t border-border pt-6">
                <div className="bg-green-500/10 p-4 rounded-lg text-center">
                  <CheckCircle className="h-8 w-8 text-green-400 mx-auto mb-2" />
                  <p className="font-semibold text-green-400">Payment Received</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Paid via {invoice.paid_via} on {new Date(invoice.paid_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            )}

            {/* Date */}
            <div className="text-center text-xs text-muted-foreground pt-4 border-t border-border">
              Invoice created: {new Date(invoice.created_at).toLocaleDateString()}
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-xs text-muted-foreground">
            Questions? Contact us through your commission status page.
          </p>
        </div>
      </div>
    </div>
  );
}
